import { useEffect, useRef, useState } from "react";
import api from "../../api/client";

/* ──────────────────────────────────────────────────────────────────────────
 * Shared NAP completion fields — NAP Code, NAP ID, NAP Port, Vacant Ports.
 * Used by every "Complete" flow (NI + Migration) so the controls and required
 * rules stay identical across the quick dropdown, full modal, and map view.
 * ────────────────────────────────────────────────────────────────────────── */

/* Parse the NAP IDs (NP designators) encoded at the end of a NAP code.
 * "MAN001 LP1 NP1-2"     -> ["NP1", "NP2"]
 * "MAN002 LP264 NP3-4-5" -> ["NP3", "NP4", "NP5"]
 * Mirrors the backend _NP_PATTERN logic in routers/nap_locator.py. */
export function parseNapIds(napCode) {
  if (!napCode) return [];
  const m = String(napCode).trim().match(/(NP)(\d+(?:-\d+)*)\s*$/i);
  if (!m) return [];
  const prefix = m[1].toUpperCase();
  return m[2].split("-").map((n) => `${prefix}${n}`);
}

/* NAP Port choices: P1-O … P8-O */
export const NAP_PORT_OPTIONS = Array.from({ length: 8 }, (_, i) => `P${i + 1}-O`);

/* Validity gate reused by each dialog's canConfirm.
 * The upper bound is enforced dynamically by NapFieldsGroup (clamped to the NAP's
 * total ports − 1), so here we only require a non-negative integer. */
export function napFieldsComplete({ napCode, napId, napPort, vacantPorts }) {
  const v = Number(vacantPorts);
  return (
    !!(napCode && String(napCode).trim()) &&
    !!(napId && String(napId).trim()) &&
    !!(napPort && String(napPort).trim()) &&
    vacantPorts !== "" && vacantPorts !== null && vacantPorts !== undefined &&
    Number.isInteger(v) && v >= 0
  );
}

/* Normalize the vacant-ports input value into the API payload value (int|null). */
export function vacantPortsPayload(vacantPorts) {
  if (vacantPorts === "" || vacantPorts === null || vacantPorts === undefined) return null;
  const v = Number(vacantPorts);
  return Number.isFinite(v) ? v : null;
}

/* ─── Searchable NAP Code selector (NAP Locator DB) with free-text fallback ─── */
function NapCodeSelect({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchOptions = (term) => {
    setLoading(true);
    api
      .get("/nap-locator", { params: { search: term, page: 1, page_size: 20, sort_by: "nap_code", sort_dir: "asc" } })
      .then(({ data }) => setOptions(Array.isArray(data) ? data : []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  };

  const handleInput = (term) => {
    setQuery(term);
    onChange(term); // free-text fallback: typed value is accepted as-is
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(term.trim()), 250);
  };

  const handleFocus = () => {
    setOpen(true);
    if (options.length === 0) fetchOptions(query.trim());
  };

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={handleFocus}
        disabled={disabled}
        placeholder="Search NAP Code..."
        className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
      />
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-400">Searching…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">
              {query.trim() ? "No matches — value will be saved as typed" : "Type to search NAP codes"}
            </div>
          ) : (
            options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.nap_code); setQuery(o.nap_code); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                  o.nap_code === value ? "bg-blue-600 text-white hover:bg-blue-700" : "text-gray-800"
                }`}
              >
                {o.nap_code}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* Max NAP photo size — keep in sync with the backend (5 MB). */
export const MAX_NAP_PHOTO_BYTES = 5 * 1024 * 1024;

export default function NapFieldsGroup({
  napCode,
  onNapCodeChange,
  napId,
  onNapIdChange,
  napPort,
  onNapPortChange,
  vacantPorts,
  onVacantPortsChange,
  showPhoto = false,
  napPhoto = null,
  onNapPhotoChange,
  disabled = false,
  labelClassName = "block text-sm font-medium mb-1",
  fieldMargin = "mb-3",
}) {
  const napIdOptions = parseNapIds(napCode);
  const napCodeRef = useRef(napCode);

  // Total ports of the matched NAP Locator record — drives the dynamic vacant-ports max.
  const [napTotalPorts, setNapTotalPorts] = useState(null);

  // When the NAP Code changes, drop a NAP ID that no longer belongs to it.
  useEffect(() => {
    if (napCodeRef.current !== napCode) {
      napCodeRef.current = napCode;
      const opts = parseNapIds(napCode);
      if (opts.length > 0 && napId && !opts.includes(napId)) onNapIdChange("");
    }
  }, [napCode, napId, onNapIdChange]);

  // Look up the NAP Locator record's total ports for the current NAP Code so the
  // max vacant ports can be set dynamically (total ports − 1).
  useEffect(() => {
    const code = String(napCode || "").trim();
    if (!code) { setNapTotalPorts(null); return; }
    const norm = (s) => String(s).trim().toUpperCase().replace(/\s+/g, " ");
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .get("/nap-locator", { params: { search: code, page: 1, page_size: 20, sort_by: "nap_code", sort_dir: "asc" } })
        .then(({ data }) => {
          if (cancelled) return;
          const match = (Array.isArray(data) ? data : []).find((o) => norm(o.nap_code) === norm(code));
          setNapTotalPorts(match && Number.isFinite(match.no_of_ports) ? match.no_of_ports : null);
        })
        .catch(() => { if (!cancelled) setNapTotalPorts(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [napCode]);

  // Dynamic max: total ports − 1 when known, otherwise fall back to 24.
  const maxVacant = napTotalPorts && napTotalPorts > 0 ? napTotalPorts - 1 : 24;

  const handleVacantChange = (raw) => {
    if (raw === "") { onVacantPortsChange(""); return; }
    let n = Number(raw);
    if (!Number.isFinite(n)) return;
    if (n < 0) n = 0;
    if (n > maxVacant) n = maxVacant;
    onVacantPortsChange(String(n));
  };

  // NAP photo preview (object URL) + selection validation.
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoError, setPhotoError] = useState("");
  useEffect(() => {
    if (!napPhoto) { setPhotoPreview(null); return; }
    if (typeof napPhoto === "string") { setPhotoPreview(napPhoto); return; }
    const url = URL.createObjectURL(napPhoto);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [napPhoto]);

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setPhotoError("");
    if (!file.type.startsWith("image/")) { setPhotoError("Please select an image file."); return; }
    if (file.size > MAX_NAP_PHOTO_BYTES) { setPhotoError("Photo must be 5MB or smaller."); return; }
    onNapPhotoChange?.(file);
  };

  const fieldCls = `w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 ${fieldMargin}`;

  return (
    <>
      <label className={labelClassName}>NAP Code <span className="text-red-500">*</span></label>
      <div className={fieldMargin}>
        <NapCodeSelect value={napCode} onChange={onNapCodeChange} disabled={disabled} />
      </div>

      <label className={labelClassName}>NAP ID <span className="text-red-500">*</span></label>
      {napIdOptions.length > 0 ? (
        <select
          value={napId || ""}
          onChange={(e) => onNapIdChange(e.target.value)}
          disabled={disabled}
          className={`${fieldCls} bg-white`}
        >
          <option value="">— Select NAP ID —</option>
          {napIdOptions.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      ) : (
        <input
          type="text"
          value={napId || ""}
          onChange={(e) => onNapIdChange(e.target.value)}
          disabled={disabled}
          placeholder="Enter NAP ID..."
          className={fieldCls}
        />
      )}

      <label className={labelClassName}>NAP Port <span className="text-red-500">*</span></label>
      <select
        value={napPort || ""}
        onChange={(e) => onNapPortChange(e.target.value)}
        disabled={disabled}
        className={`${fieldCls} bg-white`}
      >
        <option value="">— Select NAP Port —</option>
        {NAP_PORT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>

      <label className={labelClassName}>Number of Vacant Ports <span className="text-red-500">*</span></label>
      <input
        type="number"
        min={0}
        max={maxVacant}
        value={vacantPorts ?? ""}
        onChange={(e) => handleVacantChange(e.target.value)}
        disabled={disabled}
        placeholder={`0 - ${maxVacant}`}
        className={fieldCls}
      />

      {showPhoto && (
        <>
          <label className={labelClassName}>NAP Photo <span className="text-red-500">*</span></label>
          <div className={fieldMargin}>
            {napPhoto ? (
              <div className="flex items-center gap-3">
                {photoPreview && (
                  <img src={photoPreview} alt="NAP" className="h-16 w-16 object-cover rounded border flex-shrink-0" />
                )}
                <span className="text-xs text-gray-600 truncate flex-1">
                  {typeof napPhoto === "string" ? "Photo attached" : napPhoto.name}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => { setPhotoError(""); onNapPhotoChange?.(null); }}
                    className="text-xs text-red-600 hover:underline flex-shrink-0"
                  >
                    Remove
                  </button>
                )}
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                disabled={disabled}
                onChange={handlePhotoPick}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-60"
              />
            )}
            {photoError ? (
              <p className="text-xs text-red-600 mt-1">{photoError}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">Image only, max 5MB.</p>
            )}
          </div>
        </>
      )}
    </>
  );
}
